import { Link } from 'react-router-dom'
import { FiChevronRight, FiExternalLink } from 'react-icons/fi'
import { useLatestActivities } from '@presentation/hooks/useLatestActivities'
import NewsCard from '@presentation/components/ui/NewsCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'

export default function NewsSection() {
  const { activities, loading, error } = useLatestActivities(4)

  const featured = activities[0]
  const rest = activities.slice(1)
  const mobileActivities = activities.slice(0, 2)

  return (
    <section className="box-border bg-background py-4 sm:py-6 lg:py-8">
      <div className="mx-auto flex max-w-7xl flex-col pl-8 pr-4 sm:pl-10 sm:pr-6 lg:pl-14 lg:pr-8">
        <div className="pl-2 sm:pl-4 lg:pl-5">
          <div className="flex shrink-0 items-center justify-between gap-4">
            <Link to="/news" className="no-underline">
              <h2 className="text-2xl font-bold tracking-tight text-blue sm:text-3xl">
                News
              </h2>
            </Link>
            <Link
              to="/news"
              aria-label="View all Parkinson's news"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue px-3 py-2 text-sm font-semibold text-blue no-underline transition-colors hover:border-blue hover:bg-blue/8"
            >
              View all
              <FiExternalLink className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-3 h-0.5 w-full bg-blue" />
        </div>

        {loading && <LoadingSpinner />}

        {error && (
          <p className="mt-6 text-center text-text-secondary">
            Unable to load news. Please try again later.
          </p>
        )}

        {!loading && !error && activities.length > 0 && (
          <>
            {/* Mobile: equal-height vertical list */}
            <div className="mt-4 flex min-h-0 flex-col gap-4 sm:hidden">
              {mobileActivities.map((activity) => (
                <div key={activity.uid} className="min-h-0">
                  <NewsCard
                    activity={activity}
                    variant="compact"
                    sizes="(max-width: 639px) calc(100vw - 3rem), 1px"
                  />
                </div>
              ))}
            </div>

            {/* Desktop: featured + 3 compact */}
            <div className="mt-4 hidden min-h-0 flex-col gap-4 sm:flex">
              {featured && (
                <div className="h-64 min-h-0 md:h-80 lg:h-100">
                  <NewsCard activity={featured} variant="featured" fill priority />
                </div>
              )}
              {rest.length > 0 && (
                <div className="grid min-h-0 grid-cols-2 content-start items-start gap-4 lg:grid-cols-3">
                  {rest.map((activity) => (
                    <NewsCard key={activity.uid} activity={activity} variant="inline" fill />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="mt-4 flex shrink-0 justify-center sm:mt-6">
          <Link
            to="/news"
            aria-label="See more Parkinson's news"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue px-4 py-3 text-center text-sm font-semibold leading-tight text-white no-underline shadow-sm transition-[transform,opacity,background-color] duration-200 hover:scale-[1.02] hover:bg-blue/90 hover:opacity-90 sm:text-base"
          >
            See more Parkinson&apos;s news
            <FiChevronRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  )
}
